import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';

interface PerfexSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  perfexUrl?: string;
  perfexToken?: string;
  onSave: (url: string, token: string) => void;
}

export function PerfexSyncModal({ isOpen, onClose, perfexUrl = '', perfexToken = '', onSave }: PerfexSyncModalProps) {
  const [url, setUrl] = useState(perfexUrl);
  const [token, setToken] = useState(perfexToken);
  const [isSaving, setIsSaving] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSave(url, token);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setUrl(perfexUrl);
    setToken(perfexToken);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-lg p-0 bg-transparent border-none shadow-none focus:outline-none">
        <div className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] rounded-[2.5rem] shadow-2xl w-full overflow-hidden border border-white/10 relative z-10 animate-scaleIn group/modal">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-transparent opacity-0 group-hover/modal:opacity-100 transition-opacity duration-500" />
          
          <div className="relative px-8 py-7 bg-white/5 border-b border-white/10 flex justify-between items-center overflow-hidden backdrop-blur-md">
            <div className="flex items-center gap-4 relative z-10">
              <div className="w-12 h-12 bg-blue-500/10 rounded-xl border border-blue-500/20 flex items-center justify-center shadow-[0_0_15px_rgba(37,99,235,0.2)]">
                <span className="text-xl">🔧</span>
              </div>
              <div>
                <DialogTitle className="text-2xl font-bold font-oswald text-white uppercase tracking-tight">
                  Perfex CRM
                </DialogTitle>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] font-oswald">Configurações de Sincronização</p>
              </div>
            </div>
            <button 
              onClick={handleClose} 
              className="relative z-10 text-gray-400 hover:text-white transition-all bg-white/5 hover:bg-white/10 rounded-2xl w-12 h-12 flex items-center justify-center border border-white/10 hover:rotate-90 duration-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="relative p-10 space-y-10">
            {/* Perfex URL */}
            <div className="group/field">
              <label htmlFor="perfexUrl" className="block text-[10px] font-bold text-gray-400 mb-3 uppercase tracking-[0.2em] group-focus-within/field:text-blue-400 transition-colors ml-1 font-oswald">
                URL do Perfex CRM *
              </label>
              <div className="relative">
                <input
                  id="perfexUrl"
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://seu-perfex.com"
                  required
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 transition-all text-sm text-white placeholder-gray-600 font-oswald tracking-wide"
                />
                <div className="absolute inset-0 rounded-2xl bg-blue-600/5 opacity-0 group-focus-within/field:opacity-100 pointer-events-none transition-opacity" />
              </div>
              <p className="text-[10px] text-gray-500 mt-3 ml-1 font-bold uppercase tracking-[0.15em] font-oswald">
                URL completa da sua instância Perfex CRM
              </p>
            </div>

            {/* Perfex Token */}
            <div className="group/field">
              <label htmlFor="perfexToken" className="block text-[10px] font-bold text-gray-400 mb-3 uppercase tracking-[0.2em] group-focus-within/field:text-blue-400 transition-colors ml-1 font-oswald">
                Token de API *
              </label>
              <div className="relative">
                <input
                  id="perfexToken"
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="••••••••••••••••"
                  required
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 transition-all font-mono text-sm text-white placeholder-gray-600"
                />
                <div className="absolute inset-0 rounded-2xl bg-blue-600/5 opacity-0 group-focus-within/field:opacity-100 pointer-events-none transition-opacity" />
              </div>
              <p className="text-[10px] text-gray-500 mt-3 ml-1 font-bold uppercase tracking-[0.15em] font-oswald">
                Token de API (Configurações &gt; API)
              </p>
            </div>

            {/* Instructions */}
            <div className="bg-blue-500/5 border border-blue-500/20 rounded-[2rem] p-8 backdrop-blur-md relative overflow-hidden group/instr">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-transparent opacity-0 group-hover/instr:opacity-100 transition-opacity" />
              <h3 className="relative font-bold text-blue-300 text-[10px] uppercase tracking-[0.2em] mb-6 flex items-center gap-3 font-oswald">
                <span className="p-1.5 bg-blue-500/20 rounded-lg">ℹ️</span> Como obter o token
              </h3>
              <ul className="relative text-[10px] text-gray-400 space-y-4 font-bold uppercase tracking-[0.15em] font-oswald">
                <li className="flex items-center gap-4 group/item">
                  <span className="w-2 h-2 bg-blue-600 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.6)] group-hover/item:scale-125 transition-transform"></span> 
                  Acesse seu Perfex CRM
                </li>
                <li className="flex items-center gap-4 group/item">
                  <span className="w-2 h-2 bg-blue-600 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.6)] group-hover/item:scale-125 transition-transform"></span> 
                  Vá em Configurações &gt; API
                </li>
                <li className="flex items-center gap-4 group/item">
                  <span className="w-2 h-2 bg-blue-600 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.6)] group-hover/item:scale-125 transition-transform"></span> 
                  Gere ou copie seu token
                </li>
                <li className="flex items-center gap-4 group/item">
                  <span className="w-2 h-2 bg-blue-600 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.6)] group-hover/item:scale-125 transition-transform"></span> 
                  Cole o token no campo acima
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-10 border-t border-white/10">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-8 py-4 text-gray-400 bg-white/5 rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] hover:bg-white/10 hover:text-white transition-all border border-white/10 text-[10px]"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSaving || !url || !token}
                className="flex-[1.5] px-8 py-4 bg-blue-600 text-white rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] hover:bg-blue-500 transition-all shadow-lg shadow-blue-900/20 hover:shadow-xl hover:-translate-y-1 active:scale-95 disabled:opacity-50 relative overflow-hidden group/btn text-[10px] border border-blue-500/50"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                {isSaving ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                    <span>Salvando...</span>
                  </div>
                ) : (
                  'Salvar Configuração'
                )}
              </button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
